/**
 * Helper utility for detecting and formatting error alarm codes
 */
export class MeasurementErrorCodesHelper {
    /**
     * Determines if an alarm is an error alarm based on its definition name
     */
    static isErrorAlarm(alarmDefinitionName) {
        if (!alarmDefinitionName)
            return false;
        const nameLower = alarmDefinitionName.toLowerCase();
        if (!nameLower.includes(this.ERROR_KEYWORD))
            return false;
        return !this.EXCLUDED_PATTERNS.some(pattern => nameLower.includes(pattern));
    }
    /**
     * Determines if error codes 9 and 10 should be swapped based on alarm definition name
     */
    static shouldSwapCodes9And10(alarmDefinitionName) {
        if (!alarmDefinitionName)
            return false;
        const nameLower = alarmDefinitionName.toLowerCase();
        // Don't swap if name contains 'block' or 'msensor'
        return !this.SWAP_EXCLUSION_KEYWORDS.some(keyword => nameLower.includes(keyword));
    }
    /**
     * Applies code swap logic if needed
     */
    static applyCodeSwap(errorCode, alarmDefinitionName) {
        if (!alarmDefinitionName || !this.shouldSwapCodes9And10(alarmDefinitionName)) {
            return errorCode;
        }
        // Swap 9 <-> 10
        if (errorCode === 9)
            return 10;
        if (errorCode === 10)
            return 9;
        return errorCode;
    }
    /**
     * Formats a single error code value as E## format
     */
    static formatErrorCode(value) {
        if (value == null || isNaN(value))
            return 'E??';
        const code = Math.abs(Math.floor(value));
        return `E${code.toString().padStart(2, '0')}`;
    }
    /**
     * Formats error code display for min/max values
     */
    static formatErrorCodeDisplay(minValue, maxValue, alarmDefinitionName) {
        if (minValue == null && maxValue == null)
            return 'N/A';
        if (minValue == null)
            return this.formatErrorCode(maxValue);
        if (maxValue == null)
            return this.formatErrorCode(minValue);
        // Apply swap logic to display codes
        const adjustedMin = alarmDefinitionName ? this.applyCodeSwap(minValue, alarmDefinitionName) : minValue;
        const adjustedMax = alarmDefinitionName ? this.applyCodeSwap(maxValue, alarmDefinitionName) : maxValue;
        const minCode = this.formatErrorCode(adjustedMin);
        const maxCode = this.formatErrorCode(adjustedMax);
        if (adjustedMin === adjustedMax)
            return minCode;
        return `${minCode}–${maxCode}`;
    }
}
MeasurementErrorCodesHelper.ERROR_KEYWORD = 'error';
MeasurementErrorCodesHelper.EXCLUDED_PATTERNS = ['device comms error'];
MeasurementErrorCodesHelper.SWAP_EXCLUSION_KEYWORDS = ['block', 'msensor'];
